import React, { ReactNode, useRef, useEffect, useState } from "react";
import { Box } from "@mantine/core";
import styles from "@scss/custom-components/scroll.module.scss";

interface CustomVerticalScrollProps {
  children: ReactNode;
  height: string | number;
  fadeHeight?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

export const CustomVerticalScroll: React.FC<CustomVerticalScrollProps> = ({
  children,
  height,
  fadeHeight = 10,
  className = "",
  style = {},
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });

  const fadeHeightNum =
    typeof fadeHeight === "string"
      ? parseInt(fadeHeight.replace("px", ""))
      : fadeHeight;

  const updateScrollInfo = () => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setScrollInfo({
      canScrollUp: scrollTop > 0,
      canScrollDown: scrollTop < scrollHeight - clientHeight - 1, // -1 for rounding
    });
  };

  useEffect(() => {
    // Initial check and update on mount/children change
    updateScrollInfo();

    // Also check after a small delay to ensure content is rendered
    const timeoutId = setTimeout(updateScrollInfo, 0);

    // Add ResizeObserver to detect when content size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollInfo();
    });

    if (scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [children]);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Let the natural vertical scroll behavior happen
    // but still update scroll info
    setTimeout(() => {
      updateScrollInfo();
    }, 0);
  };

  const handleScroll = () => {
    updateScrollInfo();
  };

  // Dynamic mask based on scroll position
  const getMaskStyle = () => {
    const topFade = scrollInfo.canScrollUp
      ? `transparent 0px, black ${fadeHeightNum}px`
      : `black 0px`;
    const bottomFade = scrollInfo.canScrollDown
      ? `black calc(100% - ${fadeHeightNum}px), transparent 100%`
      : `black 100%`;

    return {
      mask: `linear-gradient(to bottom, ${topFade}, ${bottomFade})`,
      WebkitMask: `linear-gradient(to bottom, ${topFade}, ${bottomFade})`,
    };
  };

  return (
    <Box
      style={{
        height,
        ...style,
      }}
      className={`${styles.container} ${className}`}
    >
      <Box
        ref={scrollRef}
        className={styles.scrollAreaVertical}
        style={getMaskStyle()}
        onWheel={handleWheel}
        onScroll={handleScroll}
      >
        {children}
      </Box>
    </Box>
  );
};
